import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
    ArrowUpDown,
    CircleAlert,
    ExternalLink,
    RefreshCw,
} from 'lucide-react';
import {
    type ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { OutreachWorkPanel } from '@apps/web/components/admin/outreaches/outreach_work_panel';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import { Input } from '@apps/web/components/ui/input';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from '@apps/web/components/ui/pagination';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    type AppTableFeatures,
    appTableFeatures,
    type ColumnFiltersState,
    createColumnHelper,
    flexRender,
    type SortingState,
    useTable,
} from '@apps/web/lib/table';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminOutreaches,
    useAdminOutreachWork,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminOutreachListResponse,
    type AdminOutreachRead,
    type components,
} from '@libraries/typescript/api_client';

const PER_PAGE = 50;
const OUTREACH_WORK_REFRESH_INTERVAL_MS = 5_000;
const OUTREACH_WORK_LIMIT = 200;

type OutreachType = components['schemas']['OutreachType'];
type OutreachStatus = components['schemas']['OutreachStatus'];
type OutreachAgentState = components['schemas']['OutreachAgentState'];
type ShiftCountComparison = components['schemas']['ShiftCountComparison'];

const OUTREACH_TYPES: Array<OutreachType> = [
    'callout',
    'recurring_shift',
    'clock_in',
    'clock_out',
    'shift_confirmation',
    'survey',
    'message',
];

const OUTREACH_STATUSES: Array<OutreachStatus> = [
    'pending',
    'paused',
    'in_progress',
    'filled',
    'unfilled',
    'cancelled',
];

const OUTREACH_AGENT_STATES: Array<OutreachAgentState> = [
    'queued',
    'running',
    'monitoring',
    'idle',
    'failed',
];

const SEARCH_DEBOUNCE_MS = 300;

const SHIFT_COUNT_COMPARISONS: Array<{
    readonly value: ShiftCountComparison;
    readonly label: string;
}> = [
    { value: 'exactly', label: 'Equals' },
    { value: 'less_than', label: 'Less than' },
    { value: 'greater_than', label: 'Greater than' },
];

export const Route = createFileRoute('/_app/admin/outreaches')({
    component: OutreachesPage,
    head: () => ({
        meta: [
            {
                title: 'Outreaches | Phoebe Admin',
            },
        ],
    }),
});

const columnHelper = createColumnHelper<AppTableFeatures, AdminOutreachRead>();

function formatLabel(value: string) {
    return value.split('_').join(' ').toLowerCase();
}

function getLocalDayStartIso() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
}

function getMsUntilNextLocalDay() {
    const now = new Date();
    const nextDay = new Date(now);
    nextDay.setHours(24, 0, 1, 0);
    return Math.max(1_000, nextDay.getTime() - now.getTime());
}

function OutreachLink({ outreach }: { readonly outreach: AdminOutreachRead }) {
    const search = {
        organizationId: outreach.organization_id,
        mode: outreach.mode,
    };

    if (outreach.outreach_type === 'survey') {
        return (
            <Button
                asChild={true}
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
            >
                <Link
                    to="/surveys/$survey-id"
                    params={{ 'survey-id': outreach.id }}
                    search={search}
                    aria-label="Open outreach"
                    title="Open outreach"
                >
                    <ExternalLink className="h-3 w-3" />
                </Link>
            </Button>
        );
    }

    return (
        <Button
            asChild={true}
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
        >
            <Link
                to="/outreaches/$callout-id"
                params={{ 'callout-id': outreach.id }}
                search={search}
                aria-label="Open outreach"
                title="Open outreach"
            >
                <ExternalLink className="h-3 w-3" />
            </Link>
        </Button>
    );
}

function OutreachesPage() {
    const [page, setPage] = useState(1);
    const [selectedOutreachTypes, setSelectedOutreachTypes] = useState<
        Array<OutreachType>
    >([]);
    const [selectedStatuses, setSelectedStatuses] = useState<
        Array<OutreachStatus>
    >([]);
    const [selectedAgentStates, setSelectedAgentStates] = useState<
        Array<OutreachAgentState>
    >([]);
    const [showUrgentOnly, setShowUrgentOnly] = useState(false);
    const [selectedShiftCountComparison, setSelectedShiftCountComparison] =
        useState<ShiftCountComparison>('exactly');
    const [shiftCountFilter, setShiftCountFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'created_at', desc: true },
    ]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [outreachWorkSince, setOutreachWorkSince] =
        useState(getLocalDayStartIso);

    const outreachWorkQuery = useAdminOutreachWork(
        {
            since: outreachWorkSince,
            limit: OUTREACH_WORK_LIMIT,
        },
        { refreshIntervalMs: OUTREACH_WORK_REFRESH_INTERVAL_MS },
    );
    const { mutate: mutateOutreachWork } = outreachWorkQuery;

    useEffect(() => {
        let timeoutId: number | undefined;

        function scheduleNextLocalDay() {
            timeoutId = window.setTimeout(() => {
                setOutreachWorkSince(getLocalDayStartIso());
                scheduleNextLocalDay();
            }, getMsUntilNextLocalDay());
        }

        scheduleNextLocalDay();
        return () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    const shiftCount = Number.parseInt(shiftCountFilter, 10);
    const filteredShiftCount =
        Number.isInteger(shiftCount) && shiftCount >= 0
            ? shiftCount
            : undefined;

    const debouncedSearch = useDebouncedValue(globalFilter, SEARCH_DEBOUNCE_MS);

    const outreachesQuery = useAdminOutreaches(
        {
            page,
            perPage: PER_PAGE,
            outreachTypes: selectedOutreachTypes,
            statuses: selectedStatuses,
            agentStates: selectedAgentStates,
            shiftCount: filteredShiftCount,
            shiftCountComparison:
                filteredShiftCount === undefined
                    ? undefined
                    : selectedShiftCountComparison,
            isUrgent: showUrgentOnly ? true : undefined,
            search: debouncedSearch.trim() || undefined,
        },
        { keepPreviousData: true },
    );
    const { isLoading, isValidating, mutate } = outreachesQuery;
    const data = outreachesQuery.data as AdminOutreachListResponse | undefined;

    const handleRefresh = useCallback(() => {
        void mutate();
        void mutateOutreachWork();
    }, [mutate, mutateOutreachWork]);

    const hasActiveFilters =
        selectedOutreachTypes.length > 0 ||
        selectedStatuses.length > 0 ||
        selectedAgentStates.length > 0 ||
        showUrgentOnly ||
        shiftCountFilter !== '' ||
        globalFilter !== '';

    const handleOutreachTypesChange = useCallback((values: Array<string>) => {
        setSelectedOutreachTypes(values as Array<OutreachType>);
        setPage(1);
    }, []);

    const handleStatusesChange = useCallback((values: Array<string>) => {
        setSelectedStatuses(values as Array<OutreachStatus>);
        setPage(1);
    }, []);

    const handleAgentStatesChange = useCallback((values: Array<string>) => {
        setSelectedAgentStates(values as Array<OutreachAgentState>);
        setPage(1);
    }, []);

    const handleUrgentOnlyChange = useCallback(() => {
        setShowUrgentOnly((current) => !current);
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setGlobalFilter(value);
        setPage(1);
    }, []);

    const handleShiftCountChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setShiftCountFilter(event.target.value);
            setPage(1);
        },
        [],
    );

    const handleShiftCountComparisonChange = useCallback((value: string) => {
        setSelectedShiftCountComparison(value as ShiftCountComparison);
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setSelectedOutreachTypes([]);
        setSelectedStatuses([]);
        setSelectedAgentStates([]);
        setShowUrgentOnly(false);
        setSelectedShiftCountComparison('exactly');
        setShiftCountFilter('');
        setGlobalFilter('');
        setPage(1);
    }, []);

    const totalOutreaches = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalOutreaches / PER_PAGE));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const outreachTypeOptions = useMemo(
        () =>
            OUTREACH_TYPES.map((type) => ({
                value: type,
                label: formatLabel(type),
            })),
        [],
    );

    const statusOptions = useMemo(
        () =>
            OUTREACH_STATUSES.map((status) => ({
                value: status,
                label: formatLabel(status),
            })),
        [],
    );

    const agentStateOptions = useMemo(
        () =>
            OUTREACH_AGENT_STATES.map((state) => ({
                value: state,
                label: formatLabel(state),
            })),
        [],
    );

    const columns = useMemo(
        () =>
            columnHelper.columns([
                columnHelper.accessor('organization_name', {
                    header: 'Organization',
                    cell: ({ row }) => (
                        <div className="flex min-w-0 items-center gap-2">
                            <span className="min-w-0 truncate">
                                {row.original.organization_name}
                            </span>
                            <OutreachLink outreach={row.original} />
                        </div>
                    ),
                }),
                columnHelper.accessor('outreach_type', {
                    header: 'Type',
                    cell: ({ row }) => (
                        <div className="flex flex-wrap items-center gap-1">
                            <Badge variant="outline" className="capitalize">
                                {formatLabel(row.original.outreach_type)}
                            </Badge>
                            {row.original.is_urgent ? (
                                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                                    Urgent
                                </Badge>
                            ) : null}
                        </div>
                    ),
                }),
                columnHelper.accessor('status', {
                    header: 'Status',
                    cell: (info) => {
                        const status = info.getValue();
                        const variant =
                            status === 'cancelled'
                                ? 'destructive'
                                : status === 'filled'
                                  ? 'secondary'
                                  : 'outline';
                        return (
                            <Badge variant={variant} className="capitalize">
                                {formatLabel(status)}
                            </Badge>
                        );
                    },
                }),
                columnHelper.accessor('created_by_user_name', {
                    header: 'Created By',
                    cell: ({ row }) => (
                        <span className="block min-w-0 truncate text-xs">
                            {row.original.created_by_user_name ?? '—'}
                        </span>
                    ),
                }),
                columnHelper.accessor('mode', {
                    header: 'Mode',
                    cell: (info) => (
                        <Badge variant="outline" className="capitalize">
                            {info.getValue()}
                        </Badge>
                    ),
                }),
                columnHelper.accessor('shift_count', {
                    header: 'Shifts',
                    cell: (info) => info.getValue(),
                }),
                columnHelper.accessor('outreach_agent_state', {
                    header: 'Agent',
                    cell: (info) => {
                        const state = info.getValue();
                        return state ? (
                            <Badge variant="outline" className="capitalize">
                                {formatLabel(state)}
                            </Badge>
                        ) : (
                            <span className="text-muted-foreground text-xs">
                                None
                            </span>
                        );
                    },
                }),
                columnHelper.accessor('created_at', {
                    header: ({ column }) => (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="-ml-3 h-6 text-xs"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <ArrowUpDown className="mr-1 h-3 w-3" />
                            Created
                        </Button>
                    ),
                    cell: ({ row }) => {
                        const created = new Date(row.original.created_at);
                        return (
                            <Tooltip>
                                <TooltipTrigger className="text-muted-foreground text-xs">
                                    {formatDistanceToNowStrict(created, {
                                        addSuffix: true,
                                    })}
                                </TooltipTrigger>
                                <TooltipContent>
                                    {created.toLocaleString()}
                                </TooltipContent>
                            </Tooltip>
                        );
                    },
                }),
            ]),
        [],
    );

    const tableData = useMemo(() => Array.from(data?.outreaches ?? []), [data]);

    const table = useTable({
        features: appTableFeatures,
        data: tableData,
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
    });

    const isFetching = isValidating && !isLoading;

    return (
        <div className="flex flex-1 flex-col gap-4 p-6">
            <div>
                <h1 className="text-xl font-semibold">Outreaches</h1>
                <p className="text-muted-foreground text-sm">
                    Track outreach runs across organizations.
                </p>
            </div>
            <OutreachWorkPanel
                summary={outreachWorkQuery.data?.summary}
                items={outreachWorkQuery.data?.items ?? []}
                isLoading={outreachWorkQuery.isLoading}
                isValidating={outreachWorkQuery.isValidating}
                onRefresh={() => {
                    void outreachWorkQuery.mutate();
                }}
            />
            <Card className="shadow-sm">
                <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg">
                                Outreaches
                            </CardTitle>
                            <CardDescription>
                                Track recent outreach runs across organizations.
                            </CardDescription>
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1 text-xs"
                                onClick={handleRefresh}
                                disabled={isValidating}
                            >
                                <RefreshCw
                                    className={cn(
                                        'h-3 w-3',
                                        isValidating ? 'animate-spin' : '',
                                    )}
                                />
                                Refresh
                            </Button>
                            {isFetching && <Skeleton className="h-4 w-8" />}
                            <Badge variant="outline" className="gap-1 text-xs">
                                <CircleAlert className="h-3 w-3" />
                                {totalOutreaches} total
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 p-0">
                    <div className="border-b px-6 py-3">
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                            <div className="min-w-0 flex-1">
                                <DataTableToolbar
                                    table={table}
                                    globalFilterPlaceholder="Search by outreach ID…"
                                    globalFilterValue={globalFilter}
                                    onGlobalFilterChange={handleSearchChange}
                                    serverSideGlobalFilter={true}
                                />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <MultiSelect
                                    options={outreachTypeOptions}
                                    selected={selectedOutreachTypes}
                                    onChange={handleOutreachTypesChange}
                                    placeholder="All types"
                                    className="w-[190px] text-sm"
                                />
                                <MultiSelect
                                    options={statusOptions}
                                    selected={selectedStatuses}
                                    onChange={handleStatusesChange}
                                    placeholder="All statuses"
                                    className="w-[190px] text-sm"
                                />
                                <MultiSelect
                                    options={agentStateOptions}
                                    selected={selectedAgentStates}
                                    onChange={handleAgentStatesChange}
                                    placeholder="All agent states"
                                    className="w-[190px] text-sm"
                                />
                                <Button
                                    variant={
                                        showUrgentOnly ? 'secondary' : 'outline'
                                    }
                                    size="sm"
                                    className="text-xs"
                                    onClick={handleUrgentOnlyChange}
                                >
                                    Urgent only
                                </Button>
                                <Select
                                    value={selectedShiftCountComparison}
                                    onValueChange={
                                        handleShiftCountComparisonChange
                                    }
                                >
                                    <SelectTrigger
                                        size="sm"
                                        className="w-[130px]"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SHIFT_COUNT_COMPARISONS.map(
                                            (comparison) => (
                                                <SelectItem
                                                    key={comparison.value}
                                                    value={comparison.value}
                                                >
                                                    {comparison.label}
                                                </SelectItem>
                                            ),
                                        )}
                                    </SelectContent>
                                </Select>
                                <Input
                                    type="number"
                                    min={0}
                                    value={shiftCountFilter}
                                    onChange={handleShiftCountChange}
                                    placeholder="Shifts"
                                    className="h-8 w-[100px] text-sm"
                                />
                                {hasActiveFilters && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs"
                                        onClick={clearFilters}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 px-6 pb-6">
                        <div className="overflow-hidden rounded-md border">
                            <table className="divide-border min-w-full table-fixed divide-y text-sm">
                                <thead className="bg-muted/40">
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(
                                                    (header) => (
                                                        <th
                                                            key={header.id}
                                                            className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold tracking-wide uppercase"
                                                        >
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                      header
                                                                          .column
                                                                          .columnDef
                                                                          .header,
                                                                      header.getContext(),
                                                                  )}
                                                        </th>
                                                    ),
                                                )}
                                            </tr>
                                        ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="even:bg-muted/10 odd:bg-background"
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-3 py-2 align-top"
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext(),
                                                        )}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                    {table.getRowModel().rows.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                className="text-muted-foreground px-3 py-12 text-center text-xs"
                                            >
                                                {isLoading
                                                    ? 'Loading outreaches…'
                                                    : 'No outreaches found.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="text-muted-foreground flex items-center justify-between border-t px-6 py-4 text-xs">
                        <span>
                            Showing {table.getRowModel().rows.length} of{' '}
                            {totalOutreaches} items
                        </span>
                        <Pagination>
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        href="#"
                                        aria-disabled={page === 1}
                                        className={
                                            page === 1
                                                ? 'pointer-events-none opacity-50'
                                                : ''
                                        }
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setPage((prev) =>
                                                Math.max(1, prev - 1),
                                            );
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem>
                                    <span className="px-2 py-1 text-sm font-medium">
                                        Page {page}
                                    </span>
                                </PaginationItem>
                                <PaginationItem>
                                    <PaginationNext
                                        href="#"
                                        aria-disabled={page >= totalPages}
                                        className={
                                            page >= totalPages
                                                ? 'pointer-events-none opacity-50'
                                                : ''
                                        }
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setPage((prev) =>
                                                Math.min(totalPages, prev + 1),
                                            );
                                        }}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
